import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ProductService } from '../services/ProductService';
import OrderService from '../services/OrderService';
import NotificationSettingsService from '../services/NotificationSettingsService';

import {
  Container,
  Grid,
  Paper,
  Typography,
  Avatar,
  Box,
  Button,
  Tab,
  Tabs,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Divider,
  Alert,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Skeleton,
  Menu,
  MenuItem,
  Chip,
  CircularProgress,
  Switch,
  Stack
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Message as MessageIcon,
  PhotoCamera as PhotoCameraIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  LocalShipping as ShippingIcon,
  Favorite as FavoriteIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  LocationOn,
  Phone,
  Email,
  Business,
  ShoppingCart as ShoppingIcon,
  ContentCopy as ContentCopyIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  Send as SendIcon,
  DoneAll as DoneAllIcon,
  Done as DoneIcon,
  OpenInNew as OpenInNewIcon,
  Notifications as NotificationsIcon,
  ShoppingBag as ShoppingBagIcon
} from '@mui/icons-material';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import ConversationService from '../services/ConversationService';

// Update the PLACEHOLDER_IMAGE constant at the top of the file
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDMTA0LjQxOCA3MCAxMDggNjYuNDE4MyAxMDggNjJDMTA4IDU3LjU4MTcgMTA0LjQxOCA1NCAxMDAgNTRDOTUuNTgxNyA1NCA5MiA1Ny41ODE3IDkyIDYyQzkyIDY2LjQxODMgOTUuNTgxNyA3MCAxMDAgNzBaIiBmaWxsPSIjQTBBMEEwIi8+CjxwYXRoIGQ9Ik0xNDAgMTQwVjEzNkMxNDAgMTI0Ljk1NCAxMzEuMDQ2IDExNiAxMjAgMTE2SDgwQzY4Ljk1NDMgMTE2IDYwIDEyNC45NTQgNjAgMTM2VjE0MEgxNDBaIiBmaWxsPSIjQTBBMEEwIi8+Cjwvc3ZnPgo=';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Add these color helper functions
const getCategoryColor = (category) => {
  const colors = {
    engine: '#2196F3',      // Blue
    transmission: '#9C27B0', // Purple
    brakes: '#F44336',      // Red
    suspension: '#FF9800',   // Orange
    electrical: '#FFC107',   // Amber
    body: '#4CAF50',        // Green
    interior: '#795548',    // Brown
    accessories: '#607D8B'   // Blue Grey
  };
  return colors[category.toLowerCase()] || '#9E9E9E';
};

const getConditionColor = (condition) => {
  const colors = {
    new: '#4CAF50',         // Green
    used: '#FF9800',        // Orange
    refurbished: '#2196F3'  // Blue
  };
  return colors[condition.toLowerCase()] || '#9E9E9E';
};

const getStockColor = (stock) => {
  if (stock > 10) return '#4CAF50';  // High stock - Green
  if (stock > 5) return '#FF9800';   // Medium stock - Orange
  return '#f44336';                  // Low stock - Red
};

// Add this helper function to determine chip color based on order status
const getOrderStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'processing':
      return 'info';
    case 'shipped':
      return 'primary';
    case 'delivered':
      return 'success';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

export default function Profile() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get tab from URL parameter or state or default to 0
  const initialTab = searchParams.get('tab') === 'messages' ? 1
    : searchParams.get('tab') === 'orders' ? 2
    : location.state?.tabIndex || 0;

  // State management
  const [tabValue, setTabValue] = useState(initialTab);
  const [editMode, setEditMode] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    name: currentUser?.name || '',
    phone: currentUser?.phone || '',
    address: currentUser?.address || '',
    company: currentUser?.company || '',
    bio: currentUser?.bio || ''
  });

  // Add these new states
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [otherParticipantName, setOtherParticipantName] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  // Quick replies configuration
  const quickReplies = [
    { key: 'yes', icon: '✓', color: 'success' },
    { key: 'no', icon: '✗', color: 'error' },
    { key: 'price', icon: '💰', color: 'warning' },
    { key: 'negotiate', icon: '🤝', color: 'info' },
    { key: 'thanks', icon: '👍', color: 'primary' }
  ];

  // Queries
  const { data: userProducts, isLoading: productsLoading } = useQuery(
    ['userProducts', currentUser?.uid],
    () => ProductService.getUserProducts(currentUser?.uid)
  );

  const { data: conversations = [], isLoading: conversationsLoading } = useQuery(
    ['conversations', currentUser?.uid],
    () => ConversationService.getUserConversations(currentUser?.uid),
    {
      enabled: !!currentUser,
      initialData: []
    }
  );

  const { data: orders, isLoading: ordersLoading } = useQuery(
    ['orders', currentUser?.uid],
    () => OrderService.getUserOrders(currentUser?.uid),
    {
      enabled: !!currentUser
    }
  );

  // Add this query for messages when a conversation is selected
  const { data: conversationMessages, isLoading: messagesLoading } = useQuery(
    ['conversationMessages', selectedConversation?.id],
    () => ConversationService.getMessages(selectedConversation?.id),
    {
      enabled: !!selectedConversation?.id,
      refetchInterval: 1000, // Refetch every second
      refetchIntervalInBackground: true
    }
  );

  // Add this query for product details
  const { data: productDetails } = useQuery(
    ['product', selectedConversation?.productId],
    () => ProductService.getProductById(selectedConversation.productId),
    {
      enabled: !!selectedConversation?.productId,
    }
  );

  // Add this query for received orders
  const { data: receivedOrders, isLoading: receivedOrdersLoading } = useQuery(
    ['receivedOrders', currentUser?.uid],
    () => OrderService.getReceivedOrders(currentUser?.uid),
    {
      enabled: !!currentUser
    }
  );

  // Stats data
  const stats = [
    { 
      label: t('profile.stats.products'), 
      value: userProducts?.length || 0, 
      icon: <StoreIcon /> 
    },
    { 
      label: t('profile.stats.messages'), 
      value: conversations?.length || 0, 
      icon: <MessageIcon /> 
    },
    { 
      label: t('profile.stats.orders'), 
      value: orders?.length || 0, 
      icon: <ShoppingIcon /> 
    },
    { 
      label: t('profile.stats.favorites'), 
      value: 5, 
      icon: <FavoriteIcon /> 
    }
  ];

  // Tabs configuration
  const tabs = [
    { id: 'products', label: t('profile.myProducts'), icon: <StoreIcon /> },
    { id: 'messages', label: t('profile.messages.title'), icon: <MessageIcon /> },
    { id: 'orders', label: t('profile.orders.orders'), icon: <ShoppingIcon /> },
    { id: 'settings', label: t('profile.settings'), icon: <SettingsIcon /> }
  ];

  // Handlers and Mutations
  const updateProfile = async (newData) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, newData);
      queryClient.invalidateQueries(['user', currentUser.uid]);
      setEditMode(false);
      enqueueSnackbar(t('profile.profileUpdated'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('profile.updateError'), { variant: 'error' });
    }
  };

  const handleProfilePhotoChange = async (event) => {
    try {
      const file = event.target.files[0];
      const storageRef = ref(storage, `users/${currentUser.uid}/profile`);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);
      await updateProfile({ photoURL });
      enqueueSnackbar(t('profile.photoUpdated'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('profile.photoUpdateError'), { variant: 'error' });
    }
  };

  const deleteProductMutation = useMutation(
    (productId) => ProductService.deleteProduct(productId),
    {
      onSuccess: () => {
        enqueueSnackbar(t('profile.productDeleted'), { variant: 'success' });
        setDeleteDialogOpen(false);
        setSelectedProduct(null);
        queryClient.invalidateQueries(['userProducts', currentUser?.uid]);
      },
      onError: (error) => {
        enqueueSnackbar(t('profile.deleteError'), { variant: 'error' });
      }
    }
  );

  const toggleVisibilityMutation = useMutation(
    (productId) => ProductService.toggleProductVisibility(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userProducts', currentUser?.uid]);
        setMenuAnchor(null);
        enqueueSnackbar(t('product.visibilityUpdated'), { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar(t('errors.updateError'), { variant: 'error' });
      }
    }
  );

  // Menu Handlers
  const handleMenuClick = (event, product) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedProduct(null);
  };

  const handleViewProduct = () => {
    navigate(`/product/${selectedProduct?.id}`);
    handleMenuClose();
  };

  const handleEditProduct = () => {
    navigate(`/product/edit/${selectedProduct?.id}`);
    handleMenuClose();
  };

  const handleToggleVisibility = () => {
    toggleVisibilityMutation.mutate(selectedProduct?.id);
  };

  const handleDeleteClick = (product) => {
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedProduct?.id) {
      deleteProductMutation.mutate(selectedProduct.id);
    }
  };

  // Add these new handlers
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      enqueueSnackbar(t('profile.passwordsDoNotMatch'), { variant: 'error' });
      return;
    }

    try {
      // Assuming you have a changePassword method in your auth context
      await currentUser.updatePassword(newPassword);
      enqueueSnackbar(t('profile.passwordChanged'), { variant: 'success' });
      setChangePasswordOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleDeleteAccount = async () => {
    try {
      // Add your account deletion logic here
      // This is just a placeholder
      await currentUser.delete();
      enqueueSnackbar(t('profile.accountDeleted'), { variant: 'success' });
      navigate('/');
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  // Message handlers
  const handleSendMessage = async (messageText) => {
    if (!messageText.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const otherParticipant = selectedConversation.participants.find(
        id => id !== currentUser.uid
      );

      await ConversationService.sendMessage({
        conversationId: selectedConversation.id,
        senderId: currentUser.uid,
        senderName: currentUser.displayName || currentUser.email,
        senderPhoto: currentUser.photoURL,
        receiverId: otherParticipant,
        message: messageText.trim(),
        productId: selectedConversation.productId
      });

      setReplyMessage('');
      queryClient.invalidateQueries(['conversationMessages', selectedConversation.id]);
      queryClient.invalidateQueries(['conversations', currentUser?.uid]);
      
      enqueueSnackbar(t('profile.messages.messagesSent'), { variant: 'success' });
    } catch (error) {
      console.error('Error sending message:', error);
      enqueueSnackbar(t('profile.messages.messageError'), { variant: 'error' });
    } finally {
      setSendingMessage(false);
    }
  };

  // Add this helper function inside the Profile component
  const formatMessageDate = (date) => {
    if (!date) return '';

    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('profile.messages.justNow');
    if (minutes < 60) return t('profile.messages.minutesAgo', { minutes });
    if (hours < 24) return t('profile.messages.hoursAgo', { hours });
    if (days === 1) return t('profile.messages.yesterday');
    if (days < 7) return t('profile.messages.daysAgo', { days });

    return date.toLocaleDateString();
  };

  // Add useRef to your imports at the top
  const messagesEndRef = useRef(null);

  // Add this scroll function
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Add this useEffect to scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  // Add this effect to fetch the other participant's name
  useEffect(() => {
    const fetchOtherParticipantName = async () => {
      if (selectedConversation?.participants) {
        const otherParticipantId = selectedConversation.participants
          .find(id => id !== currentUser.uid);
        
        if (otherParticipantId) {
          const userDoc = await getDoc(doc(db, 'users', otherParticipantId));
          setOtherParticipantName(userDoc.data()?.name || 'Unknown User');
        }
      }
    };

    fetchOtherParticipantName();
  }, [selectedConversation, currentUser]);

  // Add this function inside your Profile component
  const handleProductMenuOpen = (event, product) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  // Add this function inside your Profile component
  const handleDeleteProduct = (productId) => {
    if (productId) {
      setSelectedProduct({ id: productId });
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };

  // In your Orders tab section, add tabs for placed and received orders
  const [orderTabValue, setOrderTabValue] = useState(0);

  const handleOrderTabChange = (event, newValue) => {
    setOrderTabValue(newValue);
  };

  // Add this useEffect at the top of your component
  useEffect(() => {
    // Handle navigation from notifications
    if (location.state?.tabValue !== undefined) {
      setTabValue(location.state.tabValue);
      
      // If it's an order notification, set the order tab value
      if (location.state.orderTabValue !== undefined) {
        setOrderTabValue(location.state.orderTabValue);
      }
    }
  }, [location.state]);

  // In the profile settings section, add this code for notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: true,
    messages: true, 
    orders: true
  });

  // Add useEffect to fetch initial settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await NotificationSettingsService.getSettings();
        setNotificationSettings(settings.notifications);
      } catch (error) {
        console.error('Error fetching notification settings:', error);
      }
    };

    if (currentUser) {
      fetchSettings();
    }
  }, [currentUser]);

  // Add handler for toggling settings
  const handleSettingToggle = async (setting) => {
    try {
      console.log('Toggling setting:', setting);
      console.log('Current settings:', notificationSettings);
      
      const newSettings = {
        ...notificationSettings,
        [setting]: !notificationSettings[setting]
      };
      
      console.log('New settings to save:', newSettings);
      
      await NotificationSettingsService.updateSettings(newSettings);
      
      // Verify settings were saved
      const verifySettings = await NotificationSettingsService.getSettings();
      console.log('Verified saved settings:', verifySettings);
      
      setNotificationSettings(newSettings);
      enqueueSnackbar(t('profile.settingsUpdated'), { variant: 'success' });
    } catch (error) {
      console.error('Error updating notification settings:', error);
      enqueueSnackbar(t('profile.settingsUpdateError'), { variant: 'error' });
    }
  };

  // Main JSX
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={4}>
        {/* Profile Section */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 3, 
              borderRadius: 2,
              background: theme.palette.background.paper,
              position: 'relative',
              overflow: 'visible'
            }}
          >
            {/* Profile Photo Section */}
            <Box sx={{ position: 'relative', textAlign: 'center' }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <label htmlFor="profile-photo">
                    <IconButton
                      component="span"
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        boxShadow: theme.shadows[2]
                      }}
                    >
                      <PhotoCameraIcon />
                    </IconButton>
                  </label>
                }
              >
                <Avatar
                  src={currentUser?.photoURL}
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    mx: 'auto', 
                    mb: 2,
                    boxShadow: theme.shadows[3]
                  }}
                />
              </Badge>
              <input
                accept="image/*"
                type="file"
                id="profile-photo"
                style={{ display: 'none' }}
                onChange={handleProfilePhotoChange}
              />
            </Box>

            {/* Profile Info */}
            {editMode ? (
              <Box component="form" sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label={t('profile.labels.name')}
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('profile.labels.bio')}
                  multiline
                  rows={3}
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('profile.labels.phone')}
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('profile.labels.address')}
                  value={profileData.address}
                  onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label={t('profile.labels.company')}
                  value={profileData.company}
                  onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => setEditMode(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => updateProfile(profileData)}
                  >
                    {t('profile.buttons.saveChanges')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="h5" gutterBottom textAlign="center">
                  {currentUser?.name}
                </Typography>
                <Typography color="text.secondary" textAlign="center" paragraph>
                  {profileData.bio || t('profile.info.bio')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        <Email />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={t('profile.info.email')}
                      secondary={currentUser?.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        <Phone />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={t('profile.info.phone')}
                      secondary={profileData.phone || t('profile.info.phoneNotAdded')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        <LocationOn />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={t('profile.info.address')}
                      secondary={profileData.address || t('profile.info.addressNotAdded')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light' }}>
                        <Business />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={t('profile.info.company')}
                      secondary={profileData.company || t('profile.info.companyNotAdded')}
                    />
                  </ListItem>
                </List>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                  sx={{ mt: 2 }}
                >
                  {t('profile.buttons.editProfile')}
                </Button>
              </Box>
            )}
          </Paper>

          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {stats.map((stat, index) => (
              <Grid item xs={6} key={index}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[4]
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'primary.light',
                      mx: 'auto',
                      mb: 1
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Typography variant="h4" fontWeight="bold">
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Tabs Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ borderRadius: 2 }}>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons={isMobile ? 'auto' : false}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  icon={tab.icon}
                  label={tab.label}
                  sx={{
                    minHeight: 64,
                    '&.Mui-selected': {
                      color: 'primary.main',
                    },
                  }}
                />
              ))}
            </Tabs>

            {/* Products Tab */}
            <TabPanel value={tabValue} index={0}>
              {productsLoading ? (
                <Grid container spacing={3}>
                  {[1, 2, 3].map((n) => (
                    <Grid item key={n} xs={12} sm={6} md={4}>
                      <Skeleton variant="rectangular" height={300} />
                    </Grid>
                  ))}
                </Grid>
              ) : userProducts?.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <StoreIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('profile.noProducts')}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/product/add')}
                    sx={{ mt: 2 }}
                  >
                    {t('profile.addProduct')}
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={3} sx={{ p: 3 }}>
                  {userProducts.map((product) => (
                    <Grid item key={product.id} xs={12} sm={6} md={4}>
                      <Card 
                        sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column',
                          position: 'relative',
                          opacity: product.hidden ? 0.85 : 1,
                          transition: 'all 0.3s ease',
                          filter: product.hidden ? 'grayscale(20%)' : 'none',
                          '&::after': product.hidden ? {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            backdropFilter: 'blur(1px)',
                            zIndex: 1
                          } : {}
                        }}
                      >
                        {product.hidden && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 24,
                              right: -35,
                              transform: 'rotate(45deg)',
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              color: 'white',
                              padding: '4px 40px',
                              zIndex: 2,
                              fontWeight: 'medium',
                              fontSize: '0.875rem',
                              textTransform: 'uppercase',
                              letterSpacing: 1,
                              boxShadow: theme.shadows[2]
                            }}
                          >
                            {t('product.visibility.hidden')}
                          </Box>
                        )}

                        <Box sx={{ position: 'relative', zIndex: 2 }}>
                          <Box sx={{ position: 'relative', pt: '60%' }}>
                            <CardMedia
                              component="img"
                              image={product.images[0]}
                              alt={product.title}
                              sx={{ 
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                display: 'flex',
                                gap: 1
                              }}
                            >
                              <Chip
                                size="small"
                                label={!product.hidden ? t('product.visibility.visible') : t('product.visibility.hidden')}
                                color={!product.hidden ? 'success' : 'default'}
                                sx={{ 
                                  backgroundColor: !product.hidden ? 'rgba(46, 125, 50, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                                  color: 'white',
                                  fontWeight: 500
                                }}
                              />
                            </Box>
                          </Box>

                          <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography 
                                variant="h6" 
                                sx={{
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  mb: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.3,
                                  height: '2.6em'
                                }}
                              >
                                {product.title}
                              </Typography>
                              <Typography 
                                variant="h6" 
                                color="primary"
                                sx={{ 
                                  fontWeight: 700,
                                  fontSize: '1.25rem'
                                }}
                              >
                                ${product.price.toFixed(2)}
                              </Typography>
                            </Box>

                            <Stack 
                              direction="row" 
                              spacing={1} 
                              sx={{ 
                                flexWrap: 'wrap',
                                gap: 1,
                                mb: 2
                              }}
                            >
                              <Chip
                                size="small"
                                label={t(`product.categories.${product.category.toLowerCase()}`)}
                                sx={{ 
                                  backgroundColor: getCategoryColor(product.category),
                                  color: 'white',
                                  fontWeight: 500
                                }}
                              />
                              
                              <Chip
                                size="small"
                                label={t(`product.conditions.${product.condition.toLowerCase()}`)}
                                sx={{ 
                                  backgroundColor: getConditionColor(product.condition),
                                  color: 'white',
                                  fontWeight: 500
                                }}
                              />
                              
                              <Chip
                                size="small"
                                label={`${product.stock} ${t('profile.product.inStock')}`}
                                sx={{ 
                                  backgroundColor: getStockColor(product.stock),
                                  color: 'white',
                                  fontWeight: 500
                                }}
                              />
                            </Stack>
                          </CardContent>

                          <CardActions sx={{ 
                            p: 2, 
                            pt: 0, 
                            justifyContent: 'flex-end',
                            position: 'relative',
                            zIndex: 3
                          }}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, product)}
                              sx={{ 
                                color: 'text.secondary',
                                '&:hover': {
                                  backgroundColor: 'action.hover'
                                }
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </CardActions>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </TabPanel>

            {/* Orders Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ width: '100%' }}>
                <Tabs
                  value={orderTabValue}
                  onChange={handleOrderTabChange}
                  sx={{ mb: 3 }}
                >
                  <Tab label={t('profile.orders.placed')} />
                  <Tab label={t('profile.orders.received')} />
                </Tabs>

                {orderTabValue === 0 ? (
                  // Placed Orders
                  <Box>
                    {ordersLoading ? (
                      <CircularProgress />
                    ) : orders?.length === 0 ? (
                      <Typography color="text.secondary" align="center">
                        {t('profile.noOrders')}
                      </Typography>
                    ) : (
                      orders?.map((order) => (
                        <Paper key={order.id} sx={{ p: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle1">
                              {t('profile.order.orderId', { id: order.id })}
                            </Typography>
                            <Chip
                              label={t(`profile.order.status.${order.status}`)}
                              color={getOrderStatusColor(order.status)}
                              size="small"
                            />
                          </Box>
                          
                          {/* Show only items from the current seller */}
                          {order.items
                            .filter(item => item.product.sellerId === currentUser.uid)
                            .map((item, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                  gap: 2,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={item.product.image || PLACEHOLDER_IMAGE}
                                  alt={item.product.title}
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                  }}
                                  onError={(e) => {
                                    e.target.src = PLACEHOLDER_IMAGE;
                                  }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="subtitle2">
                                    {item.product.title}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {t('profile.order.quantity')}: {item.quantity}
                                  </Typography>
                                </Box>
                                <Typography variant="subtitle2">
                                  ${(item.price * item.quantity).toFixed(2)}
                                </Typography>
                              </Box>
                            ))}
                          
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('profile.order.buyer')}: {order.shipping.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('profile.order.contact')}: {order.shipping.phone}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('profile.order.address')}: {
                                `${order.shipping.address}, ${order.shipping.city}, ${order.shipping.state} ${order.shipping.zipCode}`
                              }
                            </Typography>
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                ) : (
                  // Received Orders
                  <Box>
                    {receivedOrdersLoading ? (
                      <CircularProgress />
                    ) : receivedOrders?.length === 0 ? (
                      <Typography color="text.secondary" align="center">
                        {t('profile.noReceivedOrders')}
                      </Typography>
                    ) : (
                      receivedOrders?.map((order) => (
                        <Paper key={order.id} sx={{ p: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="subtitle1">
                              {t('profile.order.orderId', { id: order.id })}
                            </Typography>
                            <Chip
                              label={t(`profile.order.status.${order.status}`)}
                              color={getOrderStatusColor(order.status)}
                              size="small"
                            />
                          </Box>
                          
                          {/* Show only items from the current seller */}
                          {order.items
                            .filter(item => item.product.sellerId === currentUser.uid)
                            .map((item, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 1,
                                  gap: 2,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={item.product.image || PLACEHOLDER_IMAGE}
                                  alt={item.product.title}
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                  }}
                                  onError={(e) => {
                                    e.target.src = PLACEHOLDER_IMAGE;
                                  }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="subtitle2">
                                    {item.product.title}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {t('profile.order.quantity')}: {item.quantity}
                                  </Typography>
                                </Box>
                                <Typography variant="subtitle2">
                                  ${(item.price * item.quantity).toFixed(2)}
                                </Typography>
                              </Box>
                            ))}
                          
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('profile.order.buyer')}: {order.shipping.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('profile.order.contact')}: {order.shipping.phone}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('profile.order.address')}: {
                                `${order.shipping.address}, ${order.shipping.city}, ${order.shipping.state} ${order.shipping.zipCode}`
                              }
                            </Typography>
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Messages Tab */}
            <TabPanel value={tabValue} index={1}>
              {conversationsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !conversations || conversations.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <MessageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('profile.messages.empty')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', height: 'calc(100vh - 200px)' }}>
                  {/* Conversations List */}
                  <Box sx={{ width: 320, borderRight: 1, borderColor: 'divider' }}>
                    <List>
                      {(conversations || []).map((conversation) => (
                        <ListItem
                          key={conversation.id}
                          button
                          selected={selectedConversation?.id === conversation.id}
                          onClick={() => setSelectedConversation(conversation)}
                        >
                          <ListItemAvatar>
                            <Avatar src={conversation.lastSenderPhoto}>
                              {conversation.lastSenderName?.[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={conversation.lastSenderName}
                            secondary={
                              <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" noWrap>
                                  {conversation.lastMessage}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatMessageDate(conversation.lastMessageTime?.toDate())}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Messages View */}
                  {selectedConversation ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Conversation Header */}
                      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h6">
                          {t('profile.messages.conversationWith', { 
                            name: otherParticipantName || 'Unknown User'
                          })}
                        </Typography>
                        {productDetails && (
                          <Typography variant="body2" color="text.secondary">
                            {`${t('profile.messages.regarding')} ${productDetails.title}`}
                          </Typography>
                        )}
                      </Box>

                      {/* Messages List */}
                      <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                        {conversationMessages?.map((message) => (
                          <Box
                            key={message.id}
                            sx={{
                              display: 'flex',
                              flexDirection: message.senderId === currentUser.uid ? 'row-reverse' : 'row',
                              mb: 2
                            }}
                          >
                            <Box
                              sx={{
                                maxWidth: '70%',
                                bgcolor: message.senderId === currentUser.uid ? 'primary.main' : 'grey.100',
                                color: message.senderId === currentUser.uid ? 'white' : 'text.primary',
                                borderRadius: 2,
                                p: 2
                              }}
                            >
                              <Typography>{message.message}</Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: message.senderId === currentUser.uid ? 'primary.light' : 'text.secondary',
                                  display: 'block',
                                  mt: 0.5
                                }}
                              >
                                {formatMessageDate(message.createdAt?.toDate())}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        <div ref={messagesEndRef} />
                      </Box>

                      {/* Quick Replies and Input */}
                      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          {quickReplies.map((reply) => (
                            <Button
                              key={reply.key}
                              variant="outlined"
                              color={reply.color}
                              size="small"
                              onClick={() => handleSendMessage(t(`profile.messages.quickReplies.${reply.key}`))}
                              disabled={sendingMessage}
                              startIcon={<span>{reply.icon}</span>}
                              sx={{
                                borderRadius: '20px',
                                px: 2,
                                py: 1,
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: 1
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              {t(`profile.messages.quickReplies.${reply.key}`)}
                            </Button>
                          ))}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                          <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            size="small"
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            placeholder={t('profile.messages.typing')}
                            disabled={sendingMessage}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                              }
                            }}
                          />
                          <IconButton
                            color="primary"
                            onClick={() => handleSendMessage(replyMessage)}
                            disabled={!replyMessage.trim() || sendingMessage}
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              width: 40,
                              height: 40,
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                              '&.Mui-disabled': {
                                bgcolor: 'action.disabledBackground',
                              },
                            }}
                          >
                            {sendingMessage ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : (
                              <SendIcon />
                            )}
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography color="text.secondary">
                        {t('profile.messages.selectConversation')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </TabPanel>

            {/* Settings Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                <Typography variant="h6" gutterBottom>
                  {t('profile.accountSettings')}
                </Typography>
                
                {/* Account Settings Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                    {t('profile.security')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <LockIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('profile.changePassword')}
                        secondary={t('profile.changePasswordDesc')}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => setChangePasswordOpen(true)}
                      >
                        {t('profile.change')}
                      </Button>
                    </ListItem>
                  </List>
                </Paper>

                {/* Notification Settings */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                    {t('profile.notifications')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <NotificationsIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('profile.enableNotifications')}
                        secondary={t('profile.enableNotificationsDesc')}
                      />
                      <Switch
                        checked={notificationSettings.enabled}
                        onChange={() => handleSettingToggle('enabled')}
                      />
                    </ListItem>
                    {notificationSettings.enabled && (
                      <>
                        <ListItem>
                          <ListItemIcon>
                            <MessageIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={t('profile.messageNotifications')}
                            secondary={t('profile.messageNotificationsDesc')}
                          />
                          <Switch
                            checked={notificationSettings.messages}
                            onChange={() => handleSettingToggle('messages')}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ShoppingBagIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={t('profile.orderNotifications')}
                            secondary={t('profile.orderNotificationsDesc')}
                          />
                          <Switch
                            checked={notificationSettings.orders}
                            onChange={() => handleSettingToggle('orders')}
                          />
                        </ListItem>
                      </>
                    )}
                  </List>
                </Paper>

                {/* Privacy Settings */}
                <Paper sx={{ p: 3 }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                    {t('profile.privacy')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <VisibilityIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('profile.profileVisibility')}
                        secondary={t('profile.profileVisibilityDesc')}
                      />
                      <Switch
                        checked={profileData.publicProfile ?? true}
                        onChange={(e) => {
                          setProfileData(prev => ({
                            ...prev,
                            publicProfile: e.target.checked
                          }));
                          updateProfile({ publicProfile: e.target.checked });
                        }}
                      />
                    </ListItem>
                  </List>
                </Paper>

                {/* Danger Zone */}
                <Paper sx={{ p: 3, mt: 3, border: '1px solid', borderColor: 'error.main' }}>
                  <Typography variant="subtitle1" color="error" gutterBottom fontWeight="medium">
                    {t('profile.dangerZone')}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteAccountOpen(true)}
                  >
                    {t('profile.deleteAccount')}
                  </Button>
                </Paper>
              </Box>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {/* Product Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 200,
            borderRadius: 2,
            mt: 1
          }
        }}
      >
        <MenuItem onClick={handleViewProduct}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('profile.product.menu.view')} />
        </MenuItem>
        <MenuItem onClick={handleEditProduct}>
          <ListItemIcon>
            <EditIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={t('profile.product.menu.edit')} />
        </MenuItem>
        <MenuItem onClick={handleToggleVisibility}>
          <ListItemIcon>
            {!selectedProduct?.hidden ? (
              <VisibilityOffIcon fontSize="small" color="warning" />
            ) : (
              <VisibilityIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText 
            primary={t(
              !selectedProduct?.hidden 
                ? 'profile.product.menu.toggleVisibility.hide'
                : 'profile.product.menu.toggleVisibility.show'
            )} 
          />
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => handleDeleteClick(selectedProduct)}
          sx={{ 
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'error.lighter'
            }
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('profile.product.menu.delete')} />
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('profile.deleteProduct')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('profile.deleteConfirmation')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Change Password Dialog */}
      <Dialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('profile.changePassword')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            name="currentPassword"
            label={t('profile.currentPassword')}
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label={t('profile.newPassword')}
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label={t('profile.confirmPassword')}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleChangePassword} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Delete Account Dialog */}
      <Dialog
        open={deleteAccountOpen}
        onClose={() => setDeleteAccountOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle color="error">{t('profile.deleteAccount')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('profile.deleteAccountWarning')}
          </Typography>
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={t('profile.confirmPassword')}
            type="password"
            value={deleteAccountPassword}
            onChange={(e) => setDeleteAccountPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAccountOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            variant="contained" 
            color="error"
          >
            {t('profile.confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 